import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  subscriptionApi,
  SubscriptionPlan,
  SubscriptionPlanCreate,
  SubscriptionPlanUpdate,
  PlanStripePricesResponse,
  CreatePriceRequest,
} from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import { useToast } from "../Toast";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import PaymentsIcon from "@mui/icons-material/Payments";

type StatusFilter = "all" | "active" | "inactive";

const SubscriptionManagement: React.FC = () => {
  const { showToast } = useToast();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [planPrices, setPlanPrices] = useState<Record<string, PlanStripePricesResponse>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [confirmationPlan, setConfirmationPlan] = useState<SubscriptionPlan | null>(null);
  const [confirmationAction, setConfirmationAction] = useState<"activate" | "deactivate" | null>(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  // Price management state
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [pricePlan, setPricePlan] = useState<SubscriptionPlan | null>(null);
  const [stripePrices, setStripePrices] = useState<PlanStripePricesResponse | null>(null);
  const [isLoadingPrices, setIsLoadingPrices] = useState(false);
  const [priceFormData, setPriceFormData] = useState<CreatePriceRequest>({
    billing_cycle: "monthly",
    amount: 0,
    currency: "usd",
  });
  const [isUpdatingPrice, setIsUpdatingPrice] = useState(false);
  const [priceInputValue, setPriceInputValue] = useState("29.00");

  // Form state for create (only plan limits, no prices)
  const [formData, setFormData] = useState<SubscriptionPlanCreate>({
    name: "",
    price: 0,
    yearly_price: 0,
    max_domains: 1,
    max_clusters: 10,
    max_keywords_per_cluster: 25,
    max_users: 3,
    is_active: true,
  });

  // Form state for update
  const [updateFormData, setUpdateFormData] = useState<SubscriptionPlanUpdate>({});
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Refs to prevent duplicate fetches
  const isFetchingRef = useRef(false);
  const hasFetchedRef = useRef(false);

  const loadPlans = useCallback(async (forceRefresh = false) => {
    // Prevent concurrent fetches
    if (isFetchingRef.current) return;
    // Skip if already fetched unless force refresh
    if (hasFetchedRef.current && !forceRefresh) return;
    
    isFetchingRef.current = true;
    setIsLoading(true);
    setError("");
    try {
      const data = await subscriptionApi.listPlans(true); // Include inactive
      setPlans(data);
      
      // Fetch Stripe prices for all plans in parallel
      const pricesPromises = data.map(async (plan) => {
        try {
          const prices = await subscriptionApi.getStripePrices(plan.id);
          return { planId: plan.id, prices };
        } catch {
          // Plan might not have prices configured yet
          return null;
        }
      });
      
      const pricesResults = await Promise.all(pricesPromises);
      const pricesMap: Record<string, PlanStripePricesResponse> = {};
      for (const result of pricesResults) {
        if (result) {
          pricesMap[result.planId] = result.prices;
        }
      }
      setPlanPrices(pricesMap);
      hasFetchedRef.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load subscription plans");
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      loadPlans();
    }
  }, [loadPlans]);

  // Filter plans based on status
  const filteredPlans = useMemo(() => {
    if (statusFilter === "all") return plans;
    return plans.filter((plan) =>
      statusFilter === "active" ? plan.is_active : !plan.is_active
    );
  }, [plans, statusFilter]);

  const handleCreatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    setIsSubmitting(true);

    try {
      await subscriptionApi.createPlan(formData);
      setShowCreateModal(false);
      setFormData({
        name: "",
        price: 0,
        yearly_price: 0,
        max_domains: 1,
        max_clusters: 10,
        max_keywords_per_cluster: 25,
        max_users: 3,
        is_active: true,
      });
      showToast("success", "Plan created! Now set up Stripe prices using the Prices button.");
      loadPlans(true);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to create plan");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditPlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlan) return;

    setFormError("");
    setIsSubmitting(true);

    try {
      await subscriptionApi.updatePlan(selectedPlan.id, updateFormData);
      setShowEditModal(false);
      setSelectedPlan(null);
      setUpdateFormData({});
      showToast("success", "Plan updated successfully");
      loadPlans(true);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to update plan");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTogglePlanStatus = (plan: SubscriptionPlan) => {
    const action = plan.is_active ? "deactivate" : "activate";
    setConfirmationPlan(plan);
    setConfirmationAction(action);
    setIsConfirmationOpen(true);
  };

  const handleConfirmTogglePlanStatus = async () => {
    if (!confirmationPlan || !confirmationAction) return;

    try {
      if (confirmationPlan.is_active) {
        // Deactivate plan
        await subscriptionApi.deletePlan(confirmationPlan.id);
      } else {
        // Activate plan
        await subscriptionApi.updatePlan(confirmationPlan.id, { is_active: true });
      }
      loadPlans(true);
    } catch (err) {
      showToast(
        "error",
        err instanceof Error
          ? err.message
          : `Failed to ${confirmationAction} plan`
      );
    } finally {
      setIsConfirmationOpen(false);
      setConfirmationPlan(null);
      setConfirmationAction(null);
    }
  };

  const openEditModal = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setUpdateFormData({
      name: plan.name,
      max_domains: plan.max_domains,
      max_clusters: plan.max_clusters,
      max_keywords_per_cluster: plan.max_keywords_per_cluster,
      max_users: plan.max_users,
      is_active: plan.is_active,
    });
    setFormError("");
    setShowEditModal(true);
  };

  // Price management functions
  const openPriceModal = async (plan: SubscriptionPlan) => {
    setPricePlan(plan);
    setShowPriceModal(true);
    setIsLoadingPrices(true);
    setStripePrices(null);
    
    // Set default values (in cents)
    setPriceFormData({
      billing_cycle: "monthly",
      amount: 2900, // $29.00 default
      currency: "usd",
    });
    setPriceInputValue("29.00");

    try {
      const prices = await subscriptionApi.getStripePrices(plan.id);
      setStripePrices(prices);
      
      // Update form with current Stripe price if available
      if (prices?.monthly_price) {
        setPriceFormData({
          billing_cycle: "monthly",
          amount: prices.monthly_price.amount,
          currency: "usd",
        });
        setPriceInputValue((prices.monthly_price.amount / 100).toFixed(2));
      }
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to load Stripe prices"
      );
    } finally {
      setIsLoadingPrices(false);
    }
  };

  const handleUpdatePrice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pricePlan) return;

    setIsUpdatingPrice(true);
    try {
      const response = await subscriptionApi.createOrUpdatePrice(
        pricePlan.id,
        priceFormData
      );
      showToast("success", response.message);
      
      // Refresh prices
      const prices = await subscriptionApi.getStripePrices(pricePlan.id);
      setStripePrices(prices);
      
      // Update cached prices
      setPlanPrices(prev => ({ ...prev, [pricePlan.id]: prices }));
      
      // Refresh plans to update display prices
      loadPlans(true);
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to update price"
      );
    } finally {
      setIsUpdatingPrice(false);
    }
  };

  const formatCentsPrice = (cents: number | undefined) => {
    if (cents === undefined || cents === null) return "—";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  };

  const getLookupKey = (planName: string, cycle: string) => {
    return `${planName.toLowerCase().replace(/\s+/g, "_")}_${cycle}`;
  };

  const getPlanMonthlyPrice = (planId: string) => {
    return planPrices[planId]?.monthly_price?.amount;
  };

  const getPlanYearlyPrice = (planId: string) => {
    return planPrices[planId]?.yearly_price?.amount;
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
        <p>Loading subscription plans...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        {error}
        <button onClick={() => loadPlans(true)} style={{ marginLeft: 16 }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-section-header">
        <h2>Subscription Plans</h2>
        <div className="admin-header-actions">
          <div className="admin-filter-group">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="admin-filter-select"
            >
              <option value="all">All ({plans.length})</option>
              <option value="active">
                Active ({plans.filter((p) => p.is_active).length})
              </option>
              <option value="inactive">
                Inactive ({plans.filter((p) => !p.is_active).length})
              </option>
            </select>
          </div>
          <button
            className="admin-add-btn"
            onClick={() => {
              setFormError("");
              setShowCreateModal(true);
            }}
          >
            <span>+</span> Add Plan
          </button>
        </div>
      </div>

      {filteredPlans.length === 0 ? (
        <div className="admin-empty">
          <SubscriptionsIcon className="admin-empty-icon" sx={{ fontSize: 48 }} />
          <p>
            {statusFilter === "all"
              ? "No subscription plans found. Create your first plan to get started."
              : `No ${statusFilter} plans found.`}
          </p>
        </div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Monthly Price</th>
                <th>Yearly Price</th>
                <th>Domains</th>
                <th>Clusters</th>
                <th>Keywords</th>
                <th>Users</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlans.map((plan) => {
                const monthlyPrice = getPlanMonthlyPrice(plan.id);
                const yearlyPrice = getPlanYearlyPrice(plan.id);
                const hasPrices = monthlyPrice !== undefined || yearlyPrice !== undefined;
                
                return (
                  <tr key={plan.id}>
                    <td>
                      <strong>{plan.name}</strong>
                    </td>
                    <td>
                      {monthlyPrice !== undefined ? (
                        <>
                          <span style={{ fontWeight: 600, color: "#38a169" }}>
                            {formatCentsPrice(monthlyPrice)}
                          </span>
                          <span style={{ fontSize: "0.75rem", color: "#718096" }}>/mo</span>
                        </>
                      ) : (
                        <span style={{ color: "#f57c00", fontSize: "0.85rem" }}>Not set</span>
                      )}
                    </td>
                    <td>
                      {yearlyPrice !== undefined ? (
                        <>
                          <span style={{ fontWeight: 600, color: "#3182ce" }}>
                            {formatCentsPrice(yearlyPrice)}
                          </span>
                          <span style={{ fontSize: "0.75rem", color: "#718096" }}>/yr</span>
                        </>
                      ) : (
                        <span style={{ color: "#f57c00", fontSize: "0.85rem" }}>Not set</span>
                      )}
                    </td>
                    <td>{plan.max_domains}</td>
                    <td>{plan.max_clusters}</td>
                    <td>{plan.max_keywords_per_cluster}</td>
                    <td>{plan.max_users}</td>
                    <td>
                      <span
                        className={`badge ${
                          plan.is_active ? "badge-active" : "badge-inactive"
                        }`}
                      >
                        {plan.is_active ? "Active" : "Inactive"}
                      </span>
                      {!hasPrices && plan.is_active && (
                        <span 
                          className="badge" 
                          style={{ 
                            marginLeft: 4, 
                            background: "#fff3e0", 
                            color: "#e65100",
                            fontSize: "0.65rem"
                          }}
                        >
                          No prices
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="admin-actions">
                        <button
                          className="admin-action-btn"
                          onClick={() => openEditModal(plan)}
                          title="Edit plan limits"
                        >
                          Edit
                        </button>
                        <button
                          className="admin-action-btn"
                          onClick={() => openPriceModal(plan)}
                          title="Manage Stripe prices"
                          style={{ 
                            background: "#e3f2fd", 
                            color: "#1565c0",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px"
                          }}
                        >
                          <PaymentsIcon style={{ fontSize: 14 }} />
                          Prices
                        </button>
                        <button
                          className={`admin-action-btn ${
                            plan.is_active ? "delete" : "activate"
                          }`}
                          onClick={() => handleTogglePlanStatus(plan)}
                        >
                          {plan.is_active ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Plan Modal - Only limits, no prices */}
      {showCreateModal && (
        <div
          className="admin-modal-overlay"
          onClick={() => setShowCreateModal(false)}
        >
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create Subscription Plan</h3>
            <p style={{ fontSize: "0.875rem", color: "#718096", marginBottom: 16 }}>
              Create the plan first, then set up Stripe prices using the "Prices" button.
            </p>
            {formError && <div className="admin-error">{formError}</div>}
            <form onSubmit={handleCreatePlan}>
              <div className="admin-form-group">
                <label>Plan Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Basic, Pro"
                  required
                />
                <p style={{ fontSize: "0.75rem", color: "#718096", marginTop: 4 }}>
                  Must match one of: Basic, Pro (for Stripe Product ID mapping). Enterprise is handled via contact sales.
                </p>
              </div>
              <div className="admin-form-group">
                <label>Max Domains (Clients)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.max_domains}
                  onChange={(e) =>
                    setFormData({ ...formData, max_domains: parseInt(e.target.value) || 1 })
                  }
                  required
                />
              </div>
              <div className="admin-form-group">
                <label>Max Clusters</label>
                <input
                  type="number"
                  min="1"
                  value={formData.max_clusters}
                  onChange={(e) =>
                    setFormData({ ...formData, max_clusters: parseInt(e.target.value) || 1 })
                  }
                  required
                />
              </div>
              <div className="admin-form-group">
                <label>Max Keywords per Cluster</label>
                <input
                  type="number"
                  min="1"
                  value={formData.max_keywords_per_cluster}
                  onChange={(e) =>
                    setFormData({ ...formData, max_keywords_per_cluster: parseInt(e.target.value) || 1 })
                  }
                  required
                />
              </div>
              <div className="admin-form-group">
                <label>Max Users</label>
                <input
                  type="number"
                  min="1"
                  value={formData.max_users}
                  onChange={(e) =>
                    setFormData({ ...formData, max_users: parseInt(e.target.value) || 1 })
                  }
                  required
                />
              </div>
              <div className="admin-form-group admin-form-checkbox">
                <input
                  type="checkbox"
                  id="plan_is_active"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                />
                <label htmlFor="plan_is_active">Active</label>
              </div>
              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-modal-btn secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-modal-btn primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create Plan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Plan Modal - Only limits, no prices */}
      {showEditModal && selectedPlan && (
        <div
          className="admin-modal-overlay"
          onClick={() => setShowEditModal(false)}
        >
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Plan: {selectedPlan.name}</h3>
            <p style={{ fontSize: "0.875rem", color: "#718096", marginBottom: 16 }}>
              Edit plan limits. To change prices, use the "Prices" button.
            </p>
            {formError && <div className="admin-error">{formError}</div>}
            <form onSubmit={handleEditPlan}>
              <div className="admin-form-group">
                <label>Plan Name</label>
                <input
                  type="text"
                  value={updateFormData.name || ""}
                  onChange={(e) =>
                    setUpdateFormData({ ...updateFormData, name: e.target.value })
                  }
                  placeholder="e.g., Basic, Pro"
                />
              </div>
              <div className="admin-form-group">
                <label>Max Domains (Clients)</label>
                <input
                  type="number"
                  min="1"
                  value={updateFormData.max_domains ?? selectedPlan.max_domains}
                  onChange={(e) =>
                    setUpdateFormData({ ...updateFormData, max_domains: parseInt(e.target.value) || 1 })
                  }
                />
              </div>
              <div className="admin-form-group">
                <label>Max Clusters</label>
                <input
                  type="number"
                  min="1"
                  value={updateFormData.max_clusters ?? selectedPlan.max_clusters}
                  onChange={(e) =>
                    setUpdateFormData({ ...updateFormData, max_clusters: parseInt(e.target.value) || 1 })
                  }
                />
              </div>
              <div className="admin-form-group">
                <label>Max Keywords per Cluster</label>
                <input
                  type="number"
                  min="1"
                  value={updateFormData.max_keywords_per_cluster ?? selectedPlan.max_keywords_per_cluster}
                  onChange={(e) =>
                    setUpdateFormData({ ...updateFormData, max_keywords_per_cluster: parseInt(e.target.value) || 1 })
                  }
                />
              </div>
              <div className="admin-form-group">
                <label>Max Users</label>
                <input
                  type="number"
                  min="1"
                  value={updateFormData.max_users ?? selectedPlan.max_users}
                  onChange={(e) =>
                    setUpdateFormData({ ...updateFormData, max_users: parseInt(e.target.value) || 1 })
                  }
                />
              </div>
              <div className="admin-form-group admin-form-checkbox">
                <input
                  type="checkbox"
                  id="edit_plan_is_active"
                  checked={updateFormData.is_active ?? selectedPlan.is_active}
                  onChange={(e) =>
                    setUpdateFormData({ ...updateFormData, is_active: e.target.checked })
                  }
                />
                <label htmlFor="edit_plan_is_active">Active</label>
              </div>
              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-modal-btn secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-modal-btn primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Price Management Modal */}
      {showPriceModal && pricePlan && (
        <div
          className="admin-modal-overlay"
          onClick={() => setShowPriceModal(false)}
        >
          <div 
            className="admin-modal" 
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 600 }}
          >
            <h3>
              <PaymentsIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
              Stripe Prices: {pricePlan.name}
            </h3>
            
            {isLoadingPrices ? (
              <div style={{ textAlign: "center", padding: "32px 0" }}>
                <div className="admin-loading-spinner" style={{ margin: "0 auto" }}></div>
                <p style={{ marginTop: 12, color: "#718096" }}>Loading Stripe prices...</p>
              </div>
            ) : (
              <>
                {/* Lookup Key Info */}
                <div style={{ 
                  background: "#f7fafc", 
                  padding: 16, 
                  borderRadius: 8, 
                  marginBottom: 20,
                  fontSize: "0.875rem"
                }}>
                  <p style={{ margin: 0, fontWeight: 500, marginBottom: 8 }}>
                    Lookup Keys for this plan:
                  </p>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <code style={{ 
                      background: "#edf2f7", 
                      padding: "4px 8px", 
                      borderRadius: 4,
                      color: "#4a5568"
                    }}>
                      {getLookupKey(pricePlan.name, "monthly")}
                    </code>
                    <code style={{ 
                      background: "#edf2f7", 
                      padding: "4px 8px", 
                      borderRadius: 4,
                      color: "#4a5568"
                    }}>
                      {getLookupKey(pricePlan.name, "yearly")}
                    </code>
                  </div>
                </div>

                {/* Current Prices Display */}
                <div style={{ marginBottom: 24 }}>
                  <h4 style={{ marginBottom: 12, fontSize: "1rem" }}>Current Stripe Prices</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    {/* Monthly */}
                    <div style={{ 
                      background: stripePrices?.monthly_price ? "#e8f5e9" : "#fff3e0",
                      padding: 16,
                      borderRadius: 8,
                      border: `1px solid ${stripePrices?.monthly_price ? "#c8e6c9" : "#ffe0b2"}`
                    }}>
                      <p style={{ margin: 0, fontSize: "0.75rem", color: "#718096", textTransform: "uppercase" }}>
                        Monthly
                      </p>
                      {stripePrices?.monthly_price ? (
                        <>
                          <p style={{ margin: "4px 0 0 0", fontSize: "1.25rem", fontWeight: 600, color: "#2e7d32" }}>
                            {formatCentsPrice(stripePrices.monthly_price.amount)}
                          </p>
                          <code style={{ fontSize: "0.7rem", color: "#718096" }}>
                            {stripePrices.monthly_price.price_id}
                          </code>
                        </>
                      ) : (
                        <p style={{ margin: "4px 0 0 0", color: "#f57c00", fontWeight: 500 }}>
                          Not configured
                        </p>
                      )}
                    </div>
                    
                    {/* Yearly */}
                    <div style={{ 
                      background: stripePrices?.yearly_price ? "#e3f2fd" : "#fff3e0",
                      padding: 16,
                      borderRadius: 8,
                      border: `1px solid ${stripePrices?.yearly_price ? "#bbdefb" : "#ffe0b2"}`
                    }}>
                      <p style={{ margin: 0, fontSize: "0.75rem", color: "#718096", textTransform: "uppercase" }}>
                        Yearly
                      </p>
                      {stripePrices?.yearly_price ? (
                        <>
                          <p style={{ margin: "4px 0 0 0", fontSize: "1.25rem", fontWeight: 600, color: "#1565c0" }}>
                            {formatCentsPrice(stripePrices.yearly_price.amount)}
                          </p>
                          <code style={{ fontSize: "0.7rem", color: "#718096" }}>
                            {stripePrices.yearly_price.price_id}
                          </code>
                        </>
                      ) : (
                        <p style={{ margin: "4px 0 0 0", color: "#f57c00", fontWeight: 500 }}>
                          Not configured
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Create/Update Price Form */}
                <div style={{ 
                  borderTop: "1px solid #e2e8f0", 
                  paddingTop: 20 
                }}>
                  <h4 style={{ marginBottom: 12, fontSize: "1rem" }}>Create / Update Price</h4>
                  <form onSubmit={handleUpdatePrice}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      <div className="admin-form-group">
                        <label>Billing Cycle</label>
                        <select
                          value={priceFormData.billing_cycle}
                          onChange={(e) => {
                            const cycle = e.target.value as "monthly" | "yearly";
                            const currentPrice = cycle === "monthly" 
                              ? stripePrices?.monthly_price?.amount 
                              : stripePrices?.yearly_price?.amount;
                            const amount = currentPrice || (cycle === "monthly" ? 2900 : 29000);
                            setPriceFormData({
                              ...priceFormData,
                              billing_cycle: cycle,
                              amount,
                            });
                            setPriceInputValue((amount / 100).toFixed(2));
                          }}
                          style={{ width: "100%" }}
                        >
                          <option value="monthly">Monthly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                      </div>
                      <div className="admin-form-group">
                        <label>Amount (USD)</label>
                        <div style={{ position: "relative" }}>
                          <span style={{ 
                            position: "absolute", 
                            left: 12, 
                            top: "50%", 
                            transform: "translateY(-50%)",
                            color: "#64748b",
                            fontWeight: 500
                          }}>$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={priceInputValue}
                            onChange={(e) => {
                              setPriceInputValue(e.target.value);
                              const dollars = parseFloat(e.target.value);
                              if (!isNaN(dollars)) {
                                setPriceFormData({
                                  ...priceFormData,
                                  amount: Math.round(dollars * 100),
                                });
                              }
                            }}
                            onBlur={() => {
                              const dollars = parseFloat(priceInputValue);
                              if (!isNaN(dollars) && dollars >= 0) {
                                setPriceInputValue(dollars.toFixed(2));
                                setPriceFormData({
                                  ...priceFormData,
                                  amount: Math.round(dollars * 100),
                                });
                              } else {
                                setPriceInputValue("0.00");
                                setPriceFormData({ ...priceFormData, amount: 0 });
                              }
                            }}
                            placeholder="29.00"
                            required
                            style={{ paddingLeft: 28 }}
                          />
                        </div>
                        <p style={{ fontSize: "0.75rem", color: "#718096", marginTop: 4 }}>
                          per {priceFormData.billing_cycle === "monthly" ? "month" : "year"}
                        </p>
                      </div>
                    </div>
                    <p style={{ 
                      fontSize: "0.75rem", 
                      color: "#718096", 
                      marginTop: 8,
                      marginBottom: 16,
                      background: "#fffde7",
                      padding: 12,
                      borderRadius: 6,
                      border: "1px solid #fff9c4"
                    }}>
                      <strong>Note:</strong> This creates a new Stripe Price with the lookup key 
                      and transfers it from any existing price. Existing subscribers keep their 
                      current price; new subscribers get the new price.
                    </p>
                    <div className="admin-modal-actions">
                      <button
                        type="button"
                        className="admin-modal-btn secondary"
                        onClick={() => setShowPriceModal(false)}
                      >
                        Close
                      </button>
                      <button
                        type="submit"
                        className="admin-modal-btn primary"
                        disabled={isUpdatingPrice}
                      >
                        {isUpdatingPrice ? "Updating..." : "Update Price in Stripe"}
                      </button>
                    </div>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={() => {
          setIsConfirmationOpen(false);
          setConfirmationPlan(null);
          setConfirmationAction(null);
        }}
        onConfirm={handleConfirmTogglePlanStatus}
        title={
          confirmationAction === "deactivate"
            ? "Deactivate plan"
            : "Activate plan"
        }
        message={
          confirmationAction === "deactivate"
            ? `Are you sure you want to deactivate the "${confirmationPlan?.name}" plan? Tenants using this plan will retain it until reassigned.`
            : `Are you sure you want to activate the "${confirmationPlan?.name}" plan?`
        }
        confirmText={confirmationAction === "deactivate" ? "Deactivate" : "Activate"}
        variant={confirmationAction === "deactivate" ? "danger" : "info"}
      />
    </div>
  );
};

export default SubscriptionManagement;
